import { Datum } from "../../../components/docs/Datum.js";
import { note, p, table, ul } from "../blocks.js";
import type { WikiTopic } from "../types.js";

export const earningsReaction: WikiTopic = {
  slug: "earnings-reaction",
  title: "Earnings reaction",
  summary: "A results filing the market rewarded by 6% or more, held for the drift that follows.",
  group: "strategies",
  horizon: "position",
  strategyKey: "earnings_reaction",
  sections: [
    {
      heading: "What it looks for",
      code: "earnings_reaction_long",
      blocks: [
        p(
          <>
            A company files its quarterly results. The stock's move from the last close before the
            filing to the first full session after it is measured against the equal-weight universe
            of liquid names over the same closes. When the stock beat the universe by{" "}
            <Datum>6%</Datum> or more, the row fires at the next close, and that close is the entry.
          </>,
        ),
        p(
          <>
            The trade is long only and held <Datum>63</Datum> sessions, about a quarter. There is no
            stop and no target, because none was tested. The <Datum>+63</Datum> outcome column is
            the trade's result.
          </>,
        ),
      ],
    },
    {
      heading: "The gates",
      blocks: [
        ul([
          <>
            The filing is the first quarterly income statement for that quarter, and the same basis
            already has its year-ago quarter on file. Revisions are not events.
          </>,
          <>
            The stock traded a median of <Datum>Rs 5 Cr</Datum> a day over the 60 sessions up to the
            close before the filing.
          </>,
          <>
            The entry close is not on a <Datum>5</Datum>, <Datum>10</Datum>, or <Datum>20%</Datum>{" "}
            upper band. A stock locked at the circuit has no sellers, so the close is not a price
            anyone can buy at.
          </>,
          <>
            The filing was in the database before the entry session. The results job runs at{" "}
            <Datum>20:30</Datum> IST, so a filing on day D completes its reaction at the D+1 close
            and enters at the D+2 close.
          </>,
        ]),
      ],
    },
    {
      heading: "Why it stays at info",
      blocks: [
        p(
          <>
            Every row is <Datum>info</Datum>, under the <Datum>warn</Datum> floor the alert rules
            use, so nothing reaches Telegram. The strategy is paper-traded for about three months
            before anyone puts money behind it.
          </>,
        ),
        note(
          <>
            The replay (<Datum>pnpm validate:earnings</Datum>) measured <Datum>+2.28%</Datum> net
            per trade over 63 sessions in 2019-22 and <Datum>+1.60%</Datum> in 2023-26, against the
            equal-weight universe and after the 23.76 bps round trip. About half the trades lose.
            The average comes from a few large winners, so it pays across 40 to 80 open names and
            not on any one of them.
          </>,
        ),
      ],
    },
    {
      heading: "What the row carries",
      blocks: [
        table(
          "Earnings reaction payload",
          ["Field", "What it holds"],
          [
            ["price", "The entry close, adjusted"],
            ["reactionPct", "Stock less universe over the reaction window, in points"],
            ["stockReturnPct / benchmarkReturnPct", "The two legs of the reaction"],
            ["filedOn / periodEnd", "The filing date and the quarter it reports"],
            ["holdSessions", <Datum key="h">63</Datum>],
            ["medianTurnoverCr", "60-session median traded value at the baseline, Rs Cr"],
          ],
        ),
      ],
    },
  ],
};
